import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { AuthenticationContext } from '../../components/AuthenticationContext';
import Login from '../Login';
import MonthCameraLiveHlsChart from './MonthCameraLiveHlsChart';
import MonthCameraRecordHlsChart from './MonthCameraRecordHlsChart';
import MonthCameraFastForwardHlsChart from './MonthCameraFastForwardHlsChart';
import MonthCameraLiveJpgChart from './MonthCameraLiveJpgChart';
import MonthCameraFramesChart from './MonthCameraFramesChart';
import { months } from './utils';
import MonthCameraFastForwardHlsSpeedsChart from './MonthCameraFastForwardHlsSpeedsChart';
import MonthCameraFramesPositionsChart from './MonthCameraFramesPositionsChart';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { QGL_GET_CAMERA_STAT } from '../../graphql/queries/stats';
import { useQuery } from '@apollo/client';

export default function CameraStats() {
  function useQueryParams() {
    return new URLSearchParams(useLocation().search);
  }

  const query = useQueryParams();
  const history = useHistory();
  const { authorized, removeToken } = useContext(AuthenticationContext);
  const { id } = useParams<{ id: string }>();
  const [day, setDay] = useState<number>(Number(query.get('day')));
  const [month, setMonth] = useState<number>(Number(query.get('month')));
  const [year, setYear] = useState<number>(Number(query.get('year')));
  const [viewMode, setViewMode] = useState<string>(query.get('mode') || 'count');
  const [offset, setOffset] = useState<number>(0);
  const [daysInMonth, setDaysInMonth] = useState<number>(0);

  useEffect(() => {
    if (!month || !year) {
      const date = new Date();
      setMonth(date.getUTCMonth() + 1);
      setYear(date.getUTCFullYear());
    }
  }, [month, year]);

  useEffect(() => {
    if (month != 0 && year != 0) {
      const search = `?month=${month}&year=${year}&mode=${viewMode}${day ? `&day=${day}` : ''}`;
      if (search != history.location.search) {
        history.push(`/stats/camera/${id}${search}`);
      }
    }
  }, [day, history, id, month, viewMode, year]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { data } = useQuery(QGL_GET_CAMERA_STAT, {
    onError(error) {
      if (error.graphQLErrors.findIndex((err: any) => err?.extensions?.code == 'UNAUTHENTICATED') != -1) {
        removeToken();
      } else {
        setError(error.message);
        console.error(error);
      }
      setLoading(false);
    },
    onCompleted() {
      setLoading(false);
    },
    variables: {
      cameraId: id,
      month,
      year,
    },
    skip: !month || !year || !id || !authorized,
  });

  useEffect(() => {
    if (data?.cameraStats?.camera?.timezone) {
      const dt: DateTime = DateTime.fromObject({
        year: year,
        month: month,
        day: 1,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
        zone: data.cameraStats.camera.timezone,
      });
      setOffset(Math.floor(dt.offset / 60));
      setDaysInMonth(dt.daysInMonth);
    }
  }, [data?.cameraStats?.camera?.timezone, month, year]);

  const onChangeYear = (event: any) => {
    setYear(parseInt(event.target.value));
  };

  const onChangeMonth = (event: any) => {
    setMonth(parseInt(event.target.value));
  };
  const onChangeMode = (event: any) => {
    setViewMode(event.target.value);
  };

  const onChangeDay = (_day: number): void => {
    setDay(_day);
  };

  if (!authorized) return <Login />;

  return (
    <Card>
      <CardContent>
        {month > 0 && (
          <Grid container spacing={2} alignItems="center">
            <Grid item flexGrow={1}>
              <h1>{!loading ? data?.cameraStats?.camera?.name : ''}</h1>
            </Grid>
            <Grid item>
              <FormControl fullWidth>
                <InputLabel shrink>Month</InputLabel>
                <Select label="month" fullWidth value={month} onChange={onChangeMonth}>
                  {months.map((month, index) => {
                    return (
                      <MenuItem value={index + 1} key={index}>
                        {month}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl fullWidth>
                <InputLabel shrink>Year</InputLabel>
                <Select label="year" fullWidth value={year} onChange={onChangeYear}>
                  <MenuItem value={2021}>2021</MenuItem>
                  <MenuItem value={2022}>2022</MenuItem>
                  <MenuItem value={2023}>2023</MenuItem>
                  <MenuItem value={2024}>2024</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl fullWidth>
                <InputLabel shrink>Mode</InputLabel>
                <Select label="viewMode" fullWidth value={viewMode} onChange={onChangeMode}>
                  <MenuItem value={'count'}>Access</MenuItem>
                  <MenuItem value={'transfer'}>Transfer</MenuItem>
                  <MenuItem value={'notfound'}>Offline</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}

        {error && <h3>{error}</h3>}

        {loading && (
          <Box display="flex" minHeight="300px" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        )}
        {!(loading || error) && !!data?.cameraStats?.camera?.timezone && (
          <>
            <MonthCameraLiveHlsChart
              stat={data.cameraStats}
              offset={offset}
              daysInMonth={daysInMonth}
              viewMode={viewMode}
              day={day}
              month={month}
              year={year}
              tz={data.cameraStats.camera.timezone}
              onChangeDay={onChangeDay}
            />
            <MonthCameraLiveJpgChart
              stat={data.cameraStats}
              offset={offset}
              daysInMonth={daysInMonth}
              viewMode={viewMode}
              day={day}
              month={month}
              year={year}
              tz={data.cameraStats.camera.timezone}
              onChangeDay={onChangeDay}
            />
            <MonthCameraRecordHlsChart
              stat={data.cameraStats}
              offset={offset}
              daysInMonth={daysInMonth}
              viewMode={viewMode}
              day={day}
              month={month}
              year={year}
              tz={data.cameraStats.camera.timezone}
              onChangeDay={onChangeDay}
            />
            <MonthCameraFastForwardHlsChart
              stat={data.cameraStats}
              offset={offset}
              daysInMonth={daysInMonth}
              viewMode={viewMode}
              day={day}
              month={month}
              year={year}
              tz={data.cameraStats.camera.timezone}
              onChangeDay={onChangeDay}
            />
            <MonthCameraFastForwardHlsSpeedsChart
              stat={data.cameraStats}
              offset={offset}
              daysInMonth={daysInMonth}
              day={day}
              month={month}
              year={year}
              tz={data.cameraStats.camera.timezone}
            />
            <MonthCameraFramesChart
              stat={data.cameraStats}
              offset={offset}
              daysInMonth={daysInMonth}
              viewMode={viewMode}
              day={day}
              month={month}
              year={year}
              tz={data.cameraStats.camera.timezone}
              onChangeDay={onChangeDay}
            />
            <MonthCameraFramesPositionsChart
              stat={data.cameraStats}
              offset={offset}
              daysInMonth={daysInMonth}
              day={day}
              month={month}
              year={year}
              tz={data.cameraStats.camera.timezone}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
